export type BroadcastStatus = 'draft' | 'scheduled' | 'sent' | 'cancelled';
export type BroadcastAudienceKind = 'all_active_clients' | 'selected_clients';
export type BroadcastPurpose = 'service' | 'marketing';

export interface BroadcastChannels {
  inApp: boolean;
  email: boolean;
  sms: boolean;
}

interface BroadcastChannelsApiResource {
  in_app: boolean;
  email: boolean;
  sms: boolean;
}

export interface BroadcastApiResource {
  id: string;
  title: string;
  body: string;
  channels: BroadcastChannelsApiResource;
  audience_kind: BroadcastAudienceKind;
  purpose: BroadcastPurpose;
  status: BroadcastStatus;
  scheduled_for: string | null;
  total_recipients: number;
  skipped_count: number;
  sent_at: string | null;
  created_at: string;
}

export interface Broadcast {
  id: string;
  title: string;
  body: string;
  channels: BroadcastChannels;
  audienceKind: BroadcastAudienceKind;
  purpose: BroadcastPurpose;
  status: BroadcastStatus;
  scheduledFor: string | null;
  totalRecipients: number;
  skippedCount: number;
  sentAt: string | null;
  createdAt: string;
}

export interface BroadcastChannelDelivery {
  channel: string;
  sent: number;
  failed: number;
  pending: number;
}

interface BroadcastChannelDeliveryApiResource {
  channel: string;
  sent: number;
  failed: number;
  pending: number;
}

export interface BroadcastDetailApiResource extends BroadcastApiResource {
  delivery: BroadcastChannelDeliveryApiResource[];
  skipped_no_contact: number;
  skipped_opted_out: number;
  skipped_no_consent: number;
}

export interface BroadcastDetail extends Broadcast {
  delivery: BroadcastChannelDelivery[];
  skippedNoContact: number;
  skippedOptedOut: number;
  skippedNoConsent: number;
}

export interface CreateBroadcastPayload {
  title: string;
  body: string;
  channels: BroadcastChannels;
  audienceKind: BroadcastAudienceKind;
  purpose: BroadcastPurpose;
  selectedClientIds: string[] | null;
}

export interface AudiencePreview {
  totalRecipients: number;
  consentedRecipients: number;
}

function mapChannels(resource: BroadcastChannelsApiResource): BroadcastChannels {
  return { inApp: resource.in_app, email: resource.email, sms: resource.sms };
}

export function mapApiResourceToBroadcast(resource: BroadcastApiResource): Broadcast {
  return {
    id: resource.id,
    title: resource.title,
    body: resource.body,
    channels: mapChannels(resource.channels),
    audienceKind: resource.audience_kind,
    purpose: resource.purpose,
    status: resource.status,
    scheduledFor: resource.scheduled_for,
    totalRecipients: resource.total_recipients,
    skippedCount: resource.skipped_count,
    sentAt: resource.sent_at,
    createdAt: resource.created_at,
  };
}

export function mapApiResourceToBroadcastDetail(
  resource: BroadcastDetailApiResource
): BroadcastDetail {
  return {
    ...mapApiResourceToBroadcast(resource),
    delivery: resource.delivery.map(function mapDelivery(row) {
      return { channel: row.channel, sent: row.sent, failed: row.failed, pending: row.pending };
    }),
    skippedNoContact: resource.skipped_no_contact,
    skippedOptedOut: resource.skipped_opted_out,
    skippedNoConsent: resource.skipped_no_consent,
  };
}

export function mapCreateBroadcastPayload(
  payload: CreateBroadcastPayload
): Record<string, unknown> {
  return {
    title: payload.title,
    body: payload.body,
    channels: {
      in_app: payload.channels.inApp,
      email: payload.channels.email,
      sms: payload.channels.sms,
    },
    audience_kind: payload.audienceKind,
    purpose: payload.purpose,
    selected_client_ids: payload.selectedClientIds,
  };
}
